import React, { useState, useEffect } from 'react';
import { useClient } from '../../contexts/ClientContext';
import { useToast } from '../../hooks/use-toast';
import { getApiUrl } from '../../utils/config';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Shield, Loader2, User, Edit3, Eye } from 'lucide-react';

interface TeamMember {
  id: string;
  user_id: string;
  role: string;
  user_email?: string;
  user_username?: string;
  user_full_name?: string;
}

interface EditRoleModalProps {
  isOpen: boolean;
  member?: TeamMember;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditRoleModal: React.FC<EditRoleModalProps> = ({
  isOpen,
  member,
  onClose,
  onSuccess,
}) => {
  const { currentClient } = useClient();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    if (member) {
      setSelectedRole(member.role);
    }
  }, [member]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentClient || !member) return;

    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl()}/api/v1/clients/${currentClient.id}/team/members/${member.user_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify({ role: selectedRole }),
      });

      if (response.ok) {
        toast({
          title: "Role Updated",
          description: `${member.user_username || member.user_email}'s role has been updated to ${selectedRole}`,
        });
        onSuccess();
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update role",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!member) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3 text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent dark:from-indigo-400 dark:to-purple-400">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span>Change Role</span>
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-200/50 dark:border-slate-600/50">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {member.user_full_name || member.user_username}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">{member.user_email}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="role" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                New Role
              </Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="bg-white/50 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600 focus:border-indigo-500 dark:focus:border-indigo-400">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40">
                  <SelectItem value="admin" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-red-500" />
                      <div>
                        <div className="font-medium">Admin</div>
                        <div className="text-xs text-slate-500">Full access except user management</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="editor" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20">
                    <div className="flex items-center space-x-2">
                      <Edit3 className="w-4 h-4 text-blue-500" />
                      <div>
                        <div className="font-medium">Editor</div>
                        <div className="text-xs text-slate-500">Can create and edit content</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="member" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-green-500" />
                      <div>
                        <div className="font-medium">Member</div>
                        <div className="text-xs text-slate-500">Can create content</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer" className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="font-medium">Viewer</div>
                        <div className="text-xs text-slate-500">Read-only access</div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || selectedRole === member.role}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white border-0 shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Update Role
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};